import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, ExternalLink, Check, Calendar, User, Newspaper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SectionHeader } from "@/components/ui/section-header";
import api from "@/lib/api";
import usePageTitle from "@/hooks/usePageTitle";
import { formatCurrencyShort } from "@/lib/formatCurrency";

const OFFERINGS = [
  {
    title: "Keine feste Lackierung",
    text: "Fahr den Truck, der dir gefällt — keine Pflichtlackierung und kein Dresscode auf der Straße."
  },
  {
    title: "Keine Mindestkilometer",
    text: "Fahr so viel oder wenig du willst — ohne Kilometerdruck pro Monat."
  },
  {
    title: "CarPlay-Overlay",
    text: "Navigation, Telemetrie und Musik direkt im Spiel — ohne Alt-Tab."
  },
  {
    title: "Live-Tracking",
    text: "Deine Fahrten erscheinen live auf der Club-Karte und in deinem Discord-Profil."
  },
  {
    title: "Events & Konvoys",
    text: "Regelmäßige gemeinsame Touren und Events — die Teilnahme ist freiwillig."
  },
  {
    title: "Chat & Community",
    text: "Tausch dich mit anderen Fahrern aus — im Hub-Chat und auf unserem Discord."
  }
];

// The settings endpoint returns an object keyed by setting key, e.g.
// { about: { key, value, about, ... }, vtc_info: { ... } }. Be defensive and
// look at the key's `value`, its same-named field, or any entry carrying it.
const settingVal = (settings, key) => {
  if (!settings || typeof settings !== "object") return null;
  const direct = settings[key];
  if (typeof direct === "string" && direct.trim()) return direct;
  if (direct && typeof direct === "object") {
    if (direct.value) return direct.value;
    if (direct[key]) return direct[key];
  }
  for (const entry of Object.values(settings)) {
    if (entry && typeof entry === "object" && entry[key]) return entry[key];
  }
  return null;
};

const eventCoverUrl = (event) => {
  if (!event) return null;
  if (event.cover_url) return api.imageUrl(event.cover_url);
  if (event.cover_image) return api.events.imageUrl(event.id);
  return null;
};

const slugify = (text) => {
  if (!text) return "";
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/\s+/g, "-")
    .replace(/[^\w\-]+/g, "")
    .replace(/\-\-+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");
};

const staggerContainer = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.08 }
  }
};

const staggerChild = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } }
};

const CountUpNumber = ({ end, duration = 1200, format }) => {
  const ref = useRef(null);
  const [val, setVal] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !end) return;
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting || started.current) return;
      started.current = true;
      const t0 = performance.now();
      const tick = (now) => {
        const p = Math.min((now - t0) / duration, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        setVal(end * eased);
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [end, duration]);

  return <span ref={ref} className="tabular-nums">{format ? format(val) : Math.round(val).toLocaleString("de-DE")}</span>;
};

const DiscordIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.12-.098.246-.198.373-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246-.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z" /></svg>
);


export default function LandingPage() {
  usePageTitle("Open Pipe Club — Dutch & Nordic Truckstyle");
  const [stats, setStats] = useState(null);
  const [company, setCompany] = useState(null);
  const [nextEvent, setNextEvent] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [totalRevenue, setTotalRevenue] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [latestNews, setLatestNews] = useState(null);
  const [recruitmentOpen, setRecruitmentOpen] = useState(false);
  const navigate = useNavigate();


  useEffect(() => {
    api.stats.global().then(r => setStats(r)).catch(() => { });
    api.settings.get().then(r => setCompany(r)).catch(() => { });
    api.applications.getOpen().then(r => setRecruitmentOpen(!!(r?.open ?? r))).catch(() => { });

    const fetchEvents = async () => {
      try {
        const list = await api.events.list();
        const allEvents = Array.isArray(list) ? list : (list?.data || []);
        const now = new Date();
        const upcoming = allEvents
          .filter(e => e.start_date && new Date(e.start_date) > now)
          .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
        setUpcomingEvents(upcoming);

        if (upcoming.length > 0) setNextEvent(upcoming[0]);
        else if (allEvents.length > 0) setNextEvent(allEvents[0]);
      } catch (error) {
        console.error("Could not load events", error);
      }
    };
    fetchEvents();
    api.members.list().then(r => {
      const members = r?.data || r || [];
      const total = members.reduce((s, m) => s + (m.total_revenue || 0), 0);
      setTotalRevenue(total);
    }).catch(() => { });
    api.gallery.list().then(r => {
      const imgs = Array.isArray(r) ? r : (r?.data || []);
      const shuffled = imgs.sort(() => 0.5 - Math.random());
      setGalleryImages(shuffled.slice(0, 4));
    }).catch(() => { });
    api.news.list({ visibility: "public" }).then(r => {
      const list = Array.isArray(r) ? r : (r?.data || []);
      if (Array.isArray(list) && list.length > 0) {
        setLatestNews(list[0]);
      }
    }).catch(() => { });
  }, []);

  const formatEventDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const weekday = date.toLocaleDateString("de-DE", { weekday: "short" });
    const day = date.getDate();
    const month = date.toLocaleDateString("de-DE", { month: "short" });
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${weekday}. ${day}. ${month} ${hours}:${minutes}`;
  };

  return (
    <div className="page-enter overflow-x-hidden w-full" data-testid="landing-page">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center px-4 sm:px-6 lg:px-8">
        {/* Ambient brand glow */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[720px] h-[480px] rounded-full bg-amber-500/[0.05] blur-[140px] pointer-events-none" />
        <div className="max-w-6xl mx-auto text-left relative z-20 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: "easeOut", delay: 0.15 }}
          >
            <p className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.35em] text-amber-400/90 mb-8">
              <span className="h-px w-10 bg-amber-400/70" />
              Open Pipe Club — European Truckstyle VTC
            </p>
            <h1
              className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter text-white mb-6"
              data-testid="hero-title"
            >
              Driving with Style.
            </h1>
            <p className="text-lg md:text-xl text-slate-400 font-light max-w-xl mb-12">
              Since day one, the dutch- and nordic Truck-Style is part of our club and is the reason, why we founded our community.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: "easeOut", delay: 0.3 }}
            className="flex flex-wrap items-center gap-4"
          >
            <Link to="/apply">
              <Button
                size="lg"
                data-testid="hero-team-btn"
                className="bg-amber-400 text-black font-semibold rounded-full px-8 h-12 hover:bg-amber-300 text-base"
              >
                Join the Club
                <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/events">
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-8 h-12 border-white/15 text-slate-300 hover:text-white hover:bg-white/5 text-base"
              >
                Events ansehen
              </Button>
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.55, ease: "easeOut", delay: 0.45 }}
            className="flex flex-wrap items-center gap-x-8 gap-y-2 mt-10 text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500"
          >
            <span className="flex items-center gap-2">
              <span className={`w-1.5 h-1.5 rounded-full ${recruitmentOpen ? "bg-emerald-400" : "bg-rose-500"}`} />
              Rekrutierung {recruitmentOpen ? "offen" : "geschlossen"}
            </span>
            <span>Dutch &amp; Nordic Truckstyle</span>
            <span>TruckersMP &amp; TrucklineMP</span>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-b from-transparent to-black" />
      </section>

      {/* New Stats Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-black border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <AnimatePresence mode="wait">
            {!stats || totalRevenue === null ? (
              <motion.div
                key="loading-stats"
                className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center"
              >
                {[1, 2, 3, 4].map(i => (
                  <div key={i}>
                    <Skeleton className="h-12 w-24 mx-auto mb-4" />
                    <Skeleton className="h-4 w-20 mx-auto" />
                  </div>
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="loaded-stats"
                variants={staggerContainer}
                initial="hidden"
                whileInView="show"
                viewport={{ amount: 0.5 }}
                className="grid grid-cols-2 md:grid-cols-4 md:divide-x md:divide-white/10"
              >
                <motion.div variants={staggerChild} className="text-center md:px-8 md:first:pl-0">
                  <p className="text-4xl lg:text-5xl font-bold text-white">
                    <CountUpNumber end={Number(stats?.members || 0)} />
                  </p>
                  <p className="text-[11px] text-slate-500 mt-3 uppercase tracking-[0.2em] font-bold">Mitglieder</p>
                </motion.div>
                <motion.div variants={staggerChild} className="text-center md:px-8">
                  <p className="text-4xl lg:text-5xl font-bold text-white">
                    <CountUpNumber end={Number(stats?.jobs || 0)} />
                  </p>
                  <p className="text-[11px] text-slate-500 mt-3 uppercase tracking-[0.2em] font-bold">Jobs abgeschlossen</p>
                </motion.div>
                <motion.div variants={staggerChild} className="text-center md:px-8">
                  <p className="text-4xl lg:text-5xl font-bold text-white">
                    <CountUpNumber end={Number(totalRevenue) || 0} format={formatCurrencyShort} />
                  </p>
                  <p className="text-[11px] text-slate-500 mt-3 uppercase tracking-[0.2em] font-bold">Gesamtumsatz</p>
                </motion.div>
                <motion.div variants={staggerChild} className="text-center md:px-8">
                  {nextEvent && nextEvent.start_date ? (
                    <>
                      <p className="text-4xl lg:text-5xl font-bold text-white tabular-nums">
                        {new Date(nextEvent.start_date).toLocaleDateString("de-DE", { day: "2-digit", month: "short" })}
                      </p>
                      <p className="text-[11px] text-slate-500 mt-3 uppercase tracking-[0.2em] font-bold">Nächstes Event</p>
                    </>
                  ) : (
                    <>
                      <p className="text-4xl lg:text-5xl font-bold text-slate-700 tabular-nums">--</p>
                      <p className="text-[11px] text-slate-500 mt-3 uppercase tracking-[0.2em] font-bold">Keine Events</p>
                    </>
                  )}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Info Section */}
      <section className="relative z-10 py-24 px-4 sm:px-6 lg:px-8 bg-black border-t border-white/5">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ amount: 0.5 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
          >
            <SectionHeader overline="Since Day One" title="Truckstyle ist mehr als Leidenschaft" className="mb-6" />
            <p className="text-slate-300 leading-relaxed whitespace-pre-line break-words">
              {settingVal(company, "about") || "Open Pipe Club is driven by passion and pure dedication to the dutch and nordic Truckstyle and has shaped our identity."}
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ amount: 0.5 }}
            transition={{ duration: 0.55, ease: "easeOut", delay: 0.1 }}
          >
            <div className="flex items-center gap-2.5 mb-5">
              <span className={`w-1.5 h-1.5 rounded-full ${recruitmentOpen ? "bg-emerald-400" : "bg-rose-500"}`} />
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                {recruitmentOpen ? "Rekrutierung offen" : "Rekrutierung geschlossen"}
              </span>
            </div>
            <div className="divide-y divide-zinc-900 border-y border-zinc-900">
              <a
                href="https://discord.com/invite/aKKGjtwuFt"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 py-4 group hover:bg-white/[0.02] transition-colors px-1"
              >
                <DiscordIcon className="w-5 h-5 text-[#5865F2] shrink-0" />
                <div className="min-w-0">
                  <p className="text-white font-semibold">Discord</p>
                  <p className="text-xs text-slate-500">Community, Events & Support</p>
                </div>
                <ExternalLink className="w-4 h-4 text-zinc-700 ml-auto group-hover:text-amber-400 transition-colors shrink-0" />
              </a>
              <a
                href="https://trucklinemp.com/vtc/@openpipeclub"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 py-4 group hover:bg-white/[0.02] transition-colors px-1"
              >
                <img src="/images/logos/trucklinemp.png" alt="" className="w-5 h-5 object-contain shrink-0" />
                <div className="min-w-0">
                  <p className="text-white font-semibold">TrucklineMP</p>
                  <p className="text-xs text-slate-500">Unsere primäre Simulation</p>
                </div>
                <ExternalLink className="w-4 h-4 text-zinc-700 ml-auto group-hover:text-amber-400 transition-colors shrink-0" />
              </a>
              <a
                href="https://truckersmp.com/vtc/85133"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 py-4 group hover:bg-white/[0.02] transition-colors px-1"
              >
                <img src="/images/logos/truckersmp.png" alt="" className="w-5 h-5 object-contain shrink-0" />
                <div className="min-w-0">
                  <p className="text-white font-semibold">TruckersMP</p>
                  <p className="text-xs text-slate-500">VTC #85133</p>
                </div>
                <ExternalLink className="w-4 h-4 text-zinc-700 ml-auto group-hover:text-amber-400 transition-colors shrink-0" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What we offer Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-black border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ amount: 0.3 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
          >
            <SectionHeader overline="Was wir bieten" title="Fahren, wie du willst" rule />
            <div className="divide-y divide-zinc-900 border-y border-zinc-900">
              {OFFERINGS.map(({ title, text }, i) => (
                <div
                  key={title}
                  className="grid md:grid-cols-[48px_1fr_2fr] gap-x-6 gap-y-1 md:items-baseline py-5 group"
                >
                  <span className="text-[11px] font-bold text-amber-400/80 tabular-nums pt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="text-base font-bold text-white group-hover:text-amber-400 transition-colors duration-200">
                    {title}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed md:pt-0.5">{text}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Requirements Section */}
      {settingVal(company, "requirements") && (
        <section className="relative z-10 pb-24 px-4 sm:px-6 lg:px-8 bg-black border-t border-white/5">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ amount: 0.3 }}
              transition={{ duration: 0.55, ease: "easeOut" }}
            >
              <SectionHeader overline="Join the Club" title="Unsere Voraussetzungen" rule />
              <div className="flex flex-wrap gap-2">
                {settingVal(company, "requirements")
                  .replace(/##\s*\*{0,2}.*?\*{0,2}\s*\n/g, "")
                  .split(/\r?\n/)
                  .filter(line => line.trim())
                  .map((line, i) => {
                    const cleaned = line.replace(/^\*\s*/, "").replace(/\*{1,2}(.*?)\*{1,2}/g, "$1").trim();
                    if (!cleaned) return null;
                    return (
                      <span
                        key={i}
                        className="inline-flex items-center gap-2.5 rounded-full border border-zinc-900 bg-white/[0.02] px-4 py-2.5 text-sm text-zinc-300"
                      >
                        <Check className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                        {cleaned}
                      </span>
                    );
                  })}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Upcoming Events Section */}
      {upcomingEvents.length > 0 && (
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-black border-t border-white/5">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ amount: 0.3 }}
              transition={{ duration: 0.55, ease: "easeOut" }}
            >
              <SectionHeader overline="Termine" title="Anstehende Events" rule className="mb-12" />

              {/* Event Cards */}
              <motion.div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
                variants={staggerContainer}
                initial="hidden"
                whileInView="show"
                viewport={{ amount: 0.1 }}
              >
                {upcomingEvents.slice(0, 4).map((event) => {
                  const coverUrl = eventCoverUrl(event);
                  const coordinator = event.organizer || event.organisator || event.company_name || "Open Pipe Club";
                  const gameName = event.game?.name || (typeof event.game === 'string' ? event.game : null) || "ETS2";

                  const formatLocation = (city, company, fallback) => {
                    const base = city || fallback;
                    if (base && company) return `${base} (${company})`;
                    if (base) return base;
                    if (company) return company;
                    return "TBA";
                  };

                  return (
                    <motion.div
                      key={event.id}
                      variants={staggerChild}
                      className="bg-[#0b0b0c] border border-zinc-900 hover:border-zinc-700 transition-colors duration-200 rounded-2xl overflow-hidden group flex flex-col cursor-pointer"
                      onClick={() => navigate(`/events`)}
                    >
                      <div className="relative h-40 overflow-hidden bg-gradient-to-br from-[#0c0c0e] via-[#141418] to-amber-950/30 flex items-center justify-center">
                        {coverUrl ? (
                          <img
                            src={coverUrl}
                            alt={event.title || "Event"}
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-90 group-hover:opacity-100"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center p-4 text-center">
                            <Calendar size={22} className="text-zinc-700 mb-2.5" />
                            <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-[0.2em] truncate max-w-[90%]">
                              {event.title || `${event.start_city || "TBA"} → ${event.end_city || "TBA"}`}
                            </span>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0c] via-transparent to-black/40 pointer-events-none" />
                        <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-md px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-[0.2em] text-amber-400 border border-amber-500/20 uppercase">                          {gameName}
                        </div>
                      </div>

                      <div className="p-5 flex flex-col flex-grow">
                        <p className="text-[10px] text-slate-500 uppercase tracking-[0.2em] font-bold mb-1">
                          Veranstaltet von {coordinator}
                        </p>
                        <h3 className="text-sm font-extrabold text-white uppercase tracking-wider mb-5 min-h-[2.5rem] line-clamp-2 leading-tight">
                          {event.title || `${event.start_city || "TBA"} to ${event.end_city || "TBA"}`}
                        </h3>

                        {/* Timeline */}
                        <div className="relative pl-5 space-y-4 mb-6">
                          <div className="absolute left-1.5 top-2 bottom-2 w-[1px] bg-zinc-900/80" />

                          <div className="relative">
                            <div className="absolute left-[-19px] top-1 w-2 h-2 rounded-full bg-zinc-600 border border-zinc-950" />
                            <p className="text-[10px] text-slate-500 uppercase tracking-[0.2em] font-bold">ABFAHRT</p>
                            <p className="text-[11px] font-bold text-zinc-300 uppercase tracking-wide mt-0.5 truncate">
                              {formatLocation(event.start_city, event.start_company, event.start_location || event.location)}
                            </p>
                          </div>

                          <div className="relative">
                            <div className="absolute left-[-19px] top-1 w-2 h-2 rounded-full bg-amber-400 border border-zinc-950 shadow-[0_0_6px_rgba(245,158,11,0.8)]" />
                            <p className="text-[10px] text-slate-500 uppercase tracking-[0.2em] font-bold">ZIEL</p>
                            <p className="text-[11px] font-bold text-amber-400 uppercase tracking-wide mt-0.5 truncate">
                              {formatLocation(event.end_city, event.end_company, event.end_location)}
                            </p>
                          </div>
                        </div>

                        {/* Details Grid */}
                        <div className="grid grid-cols-2 gap-4 border-t border-zinc-900 pt-4 mb-5">
                          <div>
                            <p className="text-[10px] text-slate-500 uppercase tracking-[0.2em] font-bold">ZEITPLAN</p>
                            <p className="text-[10px] font-bold text-zinc-300 mt-1 truncate">
                              {formatEventDate(event.start_date)}
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] text-slate-500 uppercase tracking-[0.2em] font-bold">KATEGORIE</p>
                            <p className="text-[10px] font-bold text-white mt-1 uppercase truncate">
                              {event.event_type || "CONVOY"}
                            </p>
                          </div>
                        </div>

                        {/* Details Link */}
                        <div className="w-full mt-auto">
                          {event.external_url ? (
                            <div className="grid grid-cols-4 gap-2">
                              <Link
                                to={`/events/${slugify(event.title || `${event.start_city}-to-${event.end_city}`)}`}
                                className="col-span-3 flex items-center justify-center gap-1 bg-amber-400 hover:bg-amber-300 text-black text-[10px] font-bold uppercase py-2.5 px-3 rounded-full transition-colors tracking-[0.2em] text-center"
                              >
                                DETAILS <ChevronRight className="w-3 h-3" />
                              </Link>
                              <a
                                href={event.external_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="col-span-1 flex items-center justify-center bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-zinc-300 text-[10px] font-bold uppercase py-2.5 rounded-full transition-colors tracking-[0.2em]"
                                title="Externe Eventseite"
                              >
                                LINK
                              </a>
                            </div>
                          ) : (
                            <Link
                              to={`/events/${slugify(event.title || `${event.start_city}-to-${event.end_city}`)}`}
                              className="flex items-center justify-center gap-1.5 w-full bg-amber-400 hover:bg-amber-300 text-black text-[10px] font-bold uppercase py-2.5 px-4 rounded-full transition-colors tracking-[0.2em] text-center"
                            >
                              MORE DETAILS <ChevronRight className="w-3.5 h-3.5" />
                            </Link>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>

              {/* Timeline */}
              <div className="relative mt-12 h-4">
                <div className="absolute top-1/2 -translate-y-1/2 left-0 w-full h-[1px] bg-zinc-900" />
                <div className="grid grid-cols-4 h-full relative">
                  {upcomingEvents.slice(0, 4).map((event) => (
                    <div key={event.id} className="flex justify-center items-center">
                      <div className="w-2.5 h-2.5 rounded-full bg-amber-400 border border-zinc-950 shadow-[0_0_6px_rgba(245,158,11,0.8)]" />
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Gallery Preview */}
      {galleryImages.length > 0 && (
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-black border-t border-white/5">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ amount: 0.3 }}
              transition={{ duration: 0.55, ease: "easeOut" }}
            >
              <SectionHeader overline="Galerie" title="Momente von der Straße" rule />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {galleryImages.map((image, i) => (
                  <motion.div
                    key={image.id}
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    viewport={{ amount: 0.5 }}
                    transition={{ duration: 0.55, ease: "easeOut", delay: i * 0.1 }}
                    className={`relative group/tile rounded-2xl overflow-hidden ring-1 ring-white/10 hover:ring-amber-400/30 transition-all duration-300 ${i === 0 ? "col-span-2 row-span-2" : ""}`}
                  >
                    <img
                      src={api.gallery.imageUrl(image.uid || image.id)}
                      onError={(e) => {
                        if (image.id && !e.target.dataset.triedId) {
                          e.target.dataset.triedId = "1";
                          e.target.src = api.gallery.imageUrl(image.id);
                        }
                      }}
                      alt={image.caption || `Gallery image ${image.id}`}
                      className={`w-full object-cover group-hover/tile:scale-105 transition-transform duration-500 ${i === 0 ? "aspect-square md:aspect-auto md:h-full" : "aspect-square"}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent opacity-0 group-hover/tile:opacity-100 transition-opacity duration-300 pointer-events-none" />
                    {image.caption && (
                      <div className="absolute bottom-0 inset-x-0 p-3 translate-y-2 opacity-0 group-hover/tile:translate-y-0 group-hover/tile:opacity-100 transition-all duration-300 pointer-events-none">
                        <p className="text-[11px] font-bold text-white truncate">{image.caption}</p>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
              <div className="mt-8 flex justify-center">
                <Link
                  to="/gallery"
                  className="inline-flex items-center gap-2 bg-white/[0.03] border border-white/10 hover:border-amber-400/40 text-zinc-300 hover:text-white rounded-full px-6 h-11 text-sm font-semibold transition-colors"
                >
                  Alle Bilder ansehen
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Latest News */}
      {latestNews && (
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-black border-t border-white/5">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ amount: 0.3 }}
              transition={{ duration: 0.55, ease: "easeOut" }}
            >
              <SectionHeader overline="News & Aktuelles" title="Das Neueste aus dem Club" rule />
              <Link
                to="/news"
                className="relative block rounded-2xl overflow-hidden border border-zinc-900 min-h-[400px] md:min-h-[440px] flex items-end group"
              >
                {latestNews.image_id ? (
                  <img
                    src={api.news.imageUrl(latestNews.id)}
                    alt={latestNews.title}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-950 to-black flex items-center justify-center">
                    <Newspaper className="w-20 h-20 text-zinc-800" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20" />

                {latestNews.created_at && (
                  <div className="absolute top-5 left-5 bg-black/70 backdrop-blur-md rounded-xl w-14 h-14 flex flex-col items-center justify-center text-white border border-white/10 shadow-lg">
                    <span className="text-xl font-bold tracking-tighter leading-none">
                      {new Date(latestNews.created_at).getDate().toString().padStart(2, "0")}
                    </span>
                    <span className="text-[10px] font-bold uppercase text-amber-400 mt-0.5">
                      {new Date(latestNews.created_at).toLocaleDateString("de-DE", { month: "short" }).replace(".", "")}
                    </span>
                  </div>
                )}

                <div className="relative z-10 p-6 sm:p-10 w-full">
                  <span className="inline-flex items-center rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-400 bg-amber-400/10 border border-amber-400/25 mb-5">
                    Aktuell
                  </span>
                  <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tighter text-white mb-3 max-w-2xl group-hover:text-amber-400 transition-colors duration-200 line-clamp-3">
                    {latestNews.title}
                  </h3>
                  <p className="text-zinc-300 text-sm sm:text-base leading-relaxed mb-6 max-w-2xl line-clamp-2">
                    {latestNews.excerpt || (latestNews.content?.length > 200 ? latestNews.content.slice(0, 200) + "..." : latestNews.content)}
                  </p>
                  <div className="flex flex-wrap items-center justify-between gap-4 pt-5 border-t border-white/10">
                    <div className="flex flex-wrap items-center gap-4 text-xs text-zinc-400">
                      {latestNews.created_at && (
                        <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-amber-400/70" />{new Date(latestNews.created_at).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })}</span>
                      )}
                      {latestNews.author && <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-amber-400/70" />{latestNews.author}</span>}
                    </div>
                    <span className="inline-flex items-center gap-2 bg-amber-400 text-black font-semibold rounded-full px-6 h-11 text-sm">
                      Weiterlesen
                      <ChevronRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>
        </section>
      )}
      {/* Closing CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-black border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ amount: 0.3 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
          >
            <p className="flex items-center justify-center gap-3 text-[11px] font-bold uppercase tracking-[0.35em] text-amber-400/90 mb-5">
              <span className="h-px w-10 bg-amber-400/70" />
              Join the Club
              <span className="h-px w-10 bg-amber-400/70" />
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white mb-4">
              Bereit, mitzufahren?
            </h2>
            <p className="text-zinc-400 max-w-xl mx-auto mb-10 leading-relaxed">
              Werde Teil des Open Pipe Club — keine Pflichtlackierung, keine Mindestkilometer, einfach fahren.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link to="/apply">
                <Button className="bg-amber-400 text-black font-semibold rounded-full px-8 h-12 hover:bg-amber-300 text-base">
                  Jetzt bewerben
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </Link>
              <a href="https://discord.com/invite/aKKGjtwuFt" target="_blank" rel="noopener noreferrer">
                <Button
                  variant="outline"
                  className="rounded-full px-8 h-12 border-white/15 text-slate-300 hover:text-white hover:bg-white/5 text-base"
                >
                  Discord beitreten
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
